import PageData, { PageEvent } from '../../Model/PageData';
import VendExp from '../../Model/VendExp';
import VendexAction from '../../Model/VendexAction';
import VendexElement, {
    VendexElementType,
    VendexImageMeta,
    VendexVideoMeta,
} from '../../Model/VendexElement';

export const MANAGED_BACKGROUND_PREFIX = 'PAGE_BACKGROUND_';
export const LEGACY_MANAGED_BACKGROUND_PREFIX = 'BG_IMAGE_';

export type BuilderValidationResult = {
    errors: string[];
    warnings: string[];
};

export const getValidationMessage = (result: BuilderValidationResult) => {
    if (result.errors.length > 0) {
        return result.errors[0];
    }

    if (result.warnings.length > 0) {
        return result.warnings[0];
    }

    return '';
};

export const getManagedBackgroundElementId = (pageId: string) => {
    return `${MANAGED_BACKGROUND_PREFIX}${pageId}`;
};

export const getLegacyManagedBackgroundElementId = (pageId: string) => {
    return `${LEGACY_MANAGED_BACKGROUND_PREFIX}${pageId}`;
};

export const isManagedBackgroundElement = (
    pageId: string,
    element?: VendexElement,
) => {
    return (
        (element?.type === VendexElementType.IMAGE ||
            element?.type === VendexElementType.VIDEO) &&
        (element.id === getManagedBackgroundElementId(pageId) ||
            element.id === getLegacyManagedBackgroundElementId(pageId))
    );
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
};

const parseSetDataKey = (payload?: string) => {
    if (!payload) {
        return '';
    }

    const [rawKey] = payload.split(',');
    return rawKey?.trim();
};

const validateVendByTokenActionList = (
    actions: VendexAction[],
    location: string,
    errors: string[],
    warnings: string[],
) => {
    const sessionKeys = new Set<string>();

    actions.forEach((action, index) => {
        if (action.type === 'SET_DATA') {
            const key = parseSetDataKey(action.payload);
            if (key) {
                sessionKeys.add(key);
            }
        }

        if (action.type !== 'VEND_BY_TOKEN') {
            return;
        }

        const hasToken = sessionKeys.has('token');
        const hasId = sessionKeys.has('id');
        const hasProduct = sessionKeys.has('product');
        const hasQuantity = sessionKeys.has('quantity');

        if (!hasToken && !hasId) {
            warnings.push(
                `${location} action ${
                    index + 1
                } uses VEND_BY_TOKEN but does not set token or id earlier in the same action flow. Use SET_DATA first.`,
            );
        }

        if (!hasToken && !hasProduct) {
            warnings.push(
                `${location} action ${
                    index + 1
                } uses VEND_BY_TOKEN without token and does not set product. Runtime will default product to "R".`,
            );
        }

        if (hasQuantity) {
            const quantityAction = actions
                .slice(0, index)
                .reverse()
                .find(
                    (candidate) =>
                        candidate.type === 'SET_DATA' &&
                        parseSetDataKey(candidate.payload) === 'quantity',
                );
            const rawQuantity = quantityAction?.payload
                ?.split(',')
                .slice(1)
                .join(',')
                .trim();

            if (rawQuantity && Number.isNaN(Number(rawQuantity))) {
                errors.push(
                    `${location} action ${
                        index + 1
                    } sets quantity to "${rawQuantity}", which is not numeric or coercible to a number.`,
                );
            }
        }
    });
};

const validatePageEvent = (
    page: PageData,
    event: PageEvent | unknown,
    eventIndex: number,
    errors: string[],
    warnings: string[],
) => {
    if (!isPlainObject(event)) {
        errors.push(`Page "${page.id}" event ${eventIndex + 1} is invalid.`);
        return;
    }

    const { trigger, actions, config } = event as PageEvent & {
        config?: unknown;
    };

    if (!trigger) {
        errors.push(
            `Page "${page.id}" event ${eventIndex + 1} is missing a trigger.`,
        );
    }

    if (!Array.isArray(actions)) {
        errors.push(
            `Page "${page.id}" event ${eventIndex + 1} is missing actions.`,
        );
    } else if (actions.length === 0) {
        warnings.push(
            `Page "${page.id}" event ${
                eventIndex + 1
            } has no actions configured.`,
        );
    }

    if (trigger !== 'onLoad' && trigger !== 'onQrScan') {
        errors.push(
            `Page "${page.id}" event ${
                eventIndex + 1
            } has an unsupported trigger.`,
        );
        return;
    }

    if (config !== undefined) {
        errors.push(
            `Page "${page.id}" event ${
                eventIndex + 1
            } must not include config for ${trigger}.`,
        );
    }
};

const validatePageBackgroundWarnings = (page: PageData, warnings: string[]) => {
    const backgroundIndex = page.elements.findIndex((element) =>
        isManagedBackgroundElement(page.id, element),
    );

    if (backgroundIndex > 0) {
        warnings.push(
            `Page "${page.id}" background media should be first in the elements array.`,
        );
    }

    if (backgroundIndex >= 0) {
        const backgroundElement = page.elements[backgroundIndex];
        const metaData = backgroundElement.metaData as
            | VendexImageMeta
            | VendexVideoMeta;
        const source = metaData?.source;
        const sourceType = metaData?.type;

        if (
            sourceType === 'DYNAMIC' &&
            (!source || !source.toString().trim())
        ) {
            warnings.push(
                `Page "${page.id}" background media is dynamic but missing a data key.`,
            );
        }
    }
};

const validatePage = (page: PageData, errors: string[], warnings: string[]) => {
    if (page.events !== undefined && !Array.isArray(page.events)) {
        errors.push(`Page "${page.id}" events must be an array.`);
        return;
    }

    page.events?.forEach((event, index) =>
        validatePageEvent(page, event, index, errors, warnings),
    );

    page.events?.forEach((event, index) =>
        validateVendByTokenActionList(
            event.actions,
            `Page "${page.id}" event ${index + 1}`,
            errors,
            warnings,
        ),
    );

    page.elements.forEach((element, index) => {
        if (!Array.isArray(element.actions) || element.actions.length === 0) {
            return;
        }

        validateVendByTokenActionList(
            element.actions,
            `Page "${page.id}" element ${index + 1} (${element.id})`,
            errors,
            warnings,
        );
    });

    validatePageBackgroundWarnings(page, warnings);
};

export const validateBuilderExperience = (
    draft: Pick<VendExp, 'pages'>,
): BuilderValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!Array.isArray(draft.pages)) {
        return {
            errors: ['Experience pages must be an array.'],
            warnings,
        };
    }

    draft.pages.forEach((page) => validatePage(page, errors, warnings));

    return { errors, warnings };
};
